import apiClient from '../api/apiClient';

const distributorService = {
  
  // Get retailer profiles with outstanding amounts
  getRetailerProfiles: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        search = '',
        operator = '',
        city = '',
        district = '',
        overdueDays = '',
        startDate = '',
        endDate = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Add optional search and filter parameters
      if (search) queryParams.append('search', search);
      if (operator) queryParams.append('operator', operator);
      if (params.beatName) queryParams.append('beat_name', params.beatName);
      if (city) queryParams.append('user_city', city);
      if (district) queryParams.append('user_district', district);
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const response = await apiClient.get(`/api/distributor/retailer-profiles/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / limit),
            currentPage: page,
            pageSize: limit
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching retailer profiles:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch retailer profiles'
      };
    }
  },

  // Get retailers with outstanding amounts only
  getRetailersWithOutstanding: async (params = {}) => {
    try {
      const response = await distributorService.getRetailerProfiles(params);
      
      if (response.success) {
        // Filter only retailers with outstanding amounts > 0
        const retailersWithOutstanding = response.data.results.filter(
          retailer => parseFloat(retailer.outstanding_amount) > 0
        );
        
        return {
          ...response,
          data: {
            ...response.data,
            results: retailersWithOutstanding,
            count: retailersWithOutstanding.length
          }
        };
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching retailers with outstanding:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch retailers with outstanding'
      };
    }
  },

  // Record payment collection (legacy method - keeping for backward compatibility)
  recordPaymentCollection: async (retailerId, paymentData) => {
    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.paymentMethod,
        reference_number: paymentData.referenceNumber || '',
        notes: paymentData.notes || '',
        collected_by: paymentData.collectedBy,
        collection_date: paymentData.collectionDate
      };

      const response = await apiClient.post(`/api/distributor/retailer-profiles/${retailerId}/collect-payment/`, payload);

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Payment collection recorded successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error recording payment collection:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to record payment collection'
      };
    }
  },

  // Record outstanding transaction (new method)
  recordOutstandingTransaction: async (transactionData) => {
    try {
      const payload = {
        retailer_profile_id: parseInt(transactionData.retailerProfileId),
        amount: parseFloat(transactionData.amount),
        order_id: transactionData.orderId,
        transaction_type: transactionData.transactionType, // DEBIT or CREDIT
        txn_type: transactionData.txnType, // ETOPUP, HANDSET, SIM_CARD, GIFT, OTHER
        payment_mode: transactionData.paymentMode, // CREDIT, CASH, UPI
        transaction_date: transactionData.transactionDate,
        remark: transactionData.remark || '',
        utr: transactionData.utr || ''
      };

      const response = await apiClient.post('/api/distributor/outstanding/transaction/', payload);

      if (response.data) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Transaction processed successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error recording outstanding transaction:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to record outstanding transaction'
      };
    }
  },

  // Get payment history for a retailer
  getPaymentHistory: async (retailerId, params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        startDate = '',
        endDate = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const response = await apiClient.get(`/api/distributor/retailer-profiles/${retailerId}/payment-history/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch payment history'
      };
    }
  },

  // Get retailer transaction history (detailed ledger)
  getRetailerTransactionHistory: async (params = {}) => {
    try {
      const {
        retailer_id,
        start_date = '',
        end_date = '',
        page = 1,
        limit = 100
      } = params;

      const queryParams = new URLSearchParams({
        retailer_id: retailer_id.toString(),
        page: page.toString(),
        limit: limit.toString()
      });

      if (start_date) queryParams.append('start_date', start_date);
      if (end_date) queryParams.append('end_date', end_date);

      const response = await apiClient.get(`/api/distributor/retailer-outstanding-ledger/?${queryParams.toString()}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch transaction history'
      };
    }
  },

  // Get retailer balance summary
  getRetailerBalanceSummary: async (retailerId) => {
    try {
      const response = await apiClient.get(`/api/distributor/retailer-outstanding/balance/${retailerId}/`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching balance summary:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch balance summary'
      };
    }
  },

  // Export retailer ledger
  exportRetailerLedger: async (params = {}) => {
    try {
      const payload = {
        retailer_id: parseInt(params.retailerId),
        start_date: params.startDate,
        end_date: params.endDate,
        format: 'excel',
        include_summary: true
      };

      const response = await apiClient.post('/api/distributor/retailer-outstanding-ledger/export/', payload, {
        responseType: 'blob' // Important for file downloads
      });

      if (response.data) {
        return {
          success: true,
          data: response.data,
          headers: response.headers,
          fileName: `retailer_${params.retailerId}_outstanding_ledger_${params.startDate}_to_${params.endDate}.xlsx`
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting ledger:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to export ledger'
      };
    }
  },

  // Get transaction details
  getTransactionDetails: async (transactionId) => {
    try {
      const response = await apiClient.get(`/api/distributor/retailer-outstanding-ledger/${transactionId}/`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch transaction details'
      };
    }
  },

  // Get outstanding summary/stats
  getOutstandingSummary: async () => {
    try {
      const response = await apiClient.get('/api/distributor/retailer-profiles/outstanding-summary/');

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching outstanding summary:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch outstanding summary'
      };
    }
  },

  // Export outstanding data
  exportOutstandingData: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params);
      
      const response = await apiClient.get(`/api/distributor/retailer-profiles/export/?${queryParams.toString()}`, {
        responseType: 'blob' // Important for file downloads
      });

      if (response.data) {
        return {
          success: true,
          data: response.data,
          headers: response.headers
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting outstanding data:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to export outstanding data'
      };
    }
  },

  // Update retailer profile (full update)
  updateRetailerProfile: async (retailerId, profileData) => {
    try {
      const payload = {
        operator: profileData.operator,
        credit_limit: parseFloat(profileData.creditLimit),
        beat_id: profileData.beat ? parseInt(profileData.beat) : null
      };

      const response = await apiClient.put(`/api/distributor/retailer-profiles/${retailerId}/`, payload);

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Retailer profile updated successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error updating retailer profile:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update retailer profile'
      };
    }
  },

  // Update retailer credit limit (partial update)
  updateCreditLimit: async (retailerId, creditLimit) => {
    try {
      const payload = {
        credit_limit: parseFloat(creditLimit)
      };

      const response = await apiClient.patch(`/api/distributor/retailer-profiles/${retailerId}/`, payload);

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Credit limit updated successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error updating credit limit:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update credit limit'
      };
    }
  },

  // Get retailer details by ID
  getRetailerById: async (retailerId) => {
    try {
      const response = await apiClient.get(`/api/distributor/retailer-profiles/${retailerId}/`);

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching retailer details:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch retailer details'
      };
    }
  },



  // Get outstanding summary
  getOutstandingSummary: async () => {
    try {
      const response = await apiClient.get('/api/distributor/outstanding/summary/');
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching outstanding summary:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch outstanding summary'
      };
    }
  },



  // Portal Config CRUD Operations
  
  // Create Portal Config
  createPortalConfig: async (portalData) => {
    try {
      const response = await apiClient.post('/api/distributor/portal-configs/', portalData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to create portal config' 
        };
      }
    } catch (error) {
      console.error('Error creating portal config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create portal config'
      };
    }
  },

  // Get Portal Configs (List)
  getPortalConfigs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await apiClient.get(`/api/distributor/portal-configs/?${queryParams.toString()}`);
      
      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching portal configs:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch portal configs'
      };
    }
  },

  // Get Portal Config by ID
  getPortalConfigById: async (id) => {
    try {
      const response = await apiClient.get(`/api/distributor/portal-configs/${id}/`);
      
      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching portal config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch portal config'
      };
    }
  },

  // Update Portal Config (Partial)
  updatePortalConfig: async (id, portalData) => {
    try {
      const response = await apiClient.patch(`/api/distributor/portal-configs/${id}/`, portalData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to update portal config' 
        };
      }
    } catch (error) {
      console.error('Error updating portal config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update portal config'
      };
    }
  },

  // Delete Portal Config
  deletePortalConfig: async (id) => {
    try {
      const response = await apiClient.delete(`/api/distributor/portal-configs/${id}/`);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to delete portal config' 
        };
      }
    } catch (error) {
      console.error('Error deleting portal config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete portal config'
      };
    }
  },

  // Test Portal Login
  testPortalLogin: async (operator) => {
    try {
      const response = await apiClient.post(`/api/distributor/portal-login/?operator=${operator}&headless=true`);

      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'Portal login failed',
          hint: response.data?.hint,
          data: response.data?.data
        };
      }
    } catch (error) {
      console.error('Error testing portal login:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Portal login automation failed',
        hint: error.response?.data?.hint,
        data: error.response?.data?.data
      };
    }
  },

  // UPI Config CRUD Operations

  // Create UPI Config
  createUPIConfig: async (upiData) => {
    try {
      const response = await apiClient.post('/api/distributor/upi-configs/', upiData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to create UPI config' 
        };
      }
    } catch (error) {
      console.error('Error creating UPI config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create UPI config'
      };
    }
  },

  // Get UPI Configs (List)
  getUPIConfigs: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams(params);
      const response = await apiClient.get(`/api/distributor/upi-configs/?${queryParams.toString()}`);
      
      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching UPI configs:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch UPI configs'
      };
    }
  },

  // Get UPI Config by ID
  getUPIConfigById: async (id) => {
    try {
      const response = await apiClient.get(`/api/distributor/upi-configs/${id}/`);
      
      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching UPI config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch UPI config'
      };
    }
  },

  // Update UPI Config (Partial)
  updateUPIConfig: async (id, upiData) => {
    try {
      const response = await apiClient.patch(`/api/distributor/upi-configs/${id}/`, upiData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message,
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to update UPI config' 
        };
      }
    } catch (error) {
      console.error('Error updating UPI config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update UPI config'
      };
    }
  },

  // Delete UPI Config
  deleteUPIConfig: async (id) => {
    try {
      const response = await apiClient.delete(`/api/distributor/upi-configs/${id}/`);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to delete UPI config' 
        };
      }
    } catch (error) {
      console.error('Error deleting UPI config:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete UPI config'
      };
    }
  },

  // Orders CRUD Operations
  
  // Get orders with pagination and filters
  getOrders: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        date_from = '',
        date_to = '',
        search = '',
        status = '',
        customer_num = '',
        order_num = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (date_from) queryParams.append('date_from', date_from);
      if (date_to) queryParams.append('date_to', date_to);
      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (customer_num) queryParams.append('customer_num', customer_num);
      if (order_num) queryParams.append('order_num', order_num);

      const response = await apiClient.get(`/api/distributor/orders/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / limit),
            currentPage: page,
            pageSize: limit
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch orders'
      };
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      const response = await apiClient.get(`/api/distributor/orders/${orderId}/`);

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch order details'
      };
    }
  },

  // Create new order
  createOrder: async (orderData) => {
    try {
      const response = await apiClient.post('/api/distributor/orders/', orderData);

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Order created successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error creating order:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create order'
      };
    }
  },

  // Update order
  updateOrder: async (orderId, orderData) => {
    try {
      const response = await apiClient.patch(`/api/distributor/orders/${orderId}/`, orderData);

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Order updated successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error updating order:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update order'
      };
    }
  },

  // Delete order
  deleteOrder: async (orderId) => {
    try {
      const response = await apiClient.delete(`/api/distributor/orders/${orderId}/`);

      if (response.status === 204 || response.data?.success) {
        return {
          success: true,
          message: 'Order deleted successfully'
        };
      } else {
        return { success: false, error: 'Failed to delete order' };
      }
    } catch (error) {
      console.error('Error deleting order:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete order'
      };
    }
  },

  // Export orders to Excel
  exportOrders: async (params = {}) => {
    try {
      const payload = {
        date_from: params.date_from || '',
        date_to: params.date_to || ''
      };

      // Add optional parameters if provided
      if (params.status) payload.status = params.status;
      if (params.search) payload.search = params.search;

      const response = await apiClient.post('/api/distributor/orders/export/', payload, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `distributor_orders_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to export orders'
      };
    }
  },

  // Import orders from Excel
  importOrders: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/api/distributor/orders/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: response.data.message || 'Orders imported successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error importing orders:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to import orders'
      };
    }
  },



  // VPA Whitelist CRUD Operations

  // Get VPA Whitelist Statistics
  getVPAStats: async () => {
    try {
      const response = await apiClient.get('/api/distributor/vpa-whitelist/stats/');
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching VPA stats:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch VPA stats'
      };
    }
  },

  // Get VPA Whitelist Entries with pagination, search & filter
  getVPAEntries: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 10,
        search = '',
        status = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);

      const response = await apiClient.get(`/api/distributor/vpa-whitelist/?${queryParams.toString()}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching VPA entries:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch VPA entries'
      };
    }
  },

  // Create VPA Entry
  createVPAEntry: async (vpaData) => {
    try {
      const response = await apiClient.post('/api/distributor/vpa-whitelist/', vpaData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'VPA whitelist entry created successfully',
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to create VPA entry' 
        };
      }
    } catch (error) {
      console.error('Error creating VPA entry:', error);
      
      // Pass through the complete error response for proper handling in the component
      if (error.response?.data) {
        throw error; // Re-throw to let component handle the detailed error
      }
      
      return { 
        success: false, 
        error: error.message || 'Failed to create VPA entry'
      };
    }
  },

  // Update VPA Entry
  updateVPAEntry: async (id, vpaData) => {
    try {
      const response = await apiClient.patch(`/api/distributor/vpa-whitelist/${id}/`, vpaData);
      
      if (response.data && response.data.success) {
        return {
          success: true,
          message: response.data.message || 'VPA entry updated successfully',
          data: response.data.data
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to update VPA entry' 
        };
      }
    } catch (error) {
      console.error('Error updating VPA entry:', error);
      
      // Pass through the complete error response for proper handling in the component
      if (error.response?.data) {
        throw error; // Re-throw to let component handle the detailed error
      }
      
      return { 
        success: false, 
        error: error.message || 'Failed to update VPA entry'
      };
    }
  },

  // Delete VPA Entry
  deleteVPAEntry: async (id) => {
    try {
      const response = await apiClient.delete(`/api/distributor/vpa-whitelist/${id}/`);
      
      if (response.status === 204 || response.data?.success) {
        return {
          success: true,
          message: 'VPA entry deleted successfully'
        };
      } else {
        return { 
          success: false, 
          error: response.data?.message || 'Failed to delete VPA entry' 
        };
      }
    } catch (error) {
      console.error('Error deleting VPA entry:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete VPA entry'
      };
    }
  },

  // Beat CRUD Operations
  
  // Create Beat
  createBeat: async (beatData) => {
    try {
      const response = await apiClient.post('/api/distributor/beats/', beatData);
      
      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Beat created successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error creating beat:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create beat'
      };
    }
  },

  // Get Beats (List)
  getBeats: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        search = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (search) queryParams.append('search', search);

      const response = await apiClient.get(`/api/distributor/beats/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results || [],
            beat_name: response.data.beat_name,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching beats:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch beats'
      };
    }
  },

  // Get Beat by ID
  getBeatById: async (id) => {
    try {
      const response = await apiClient.get(`/api/distributor/beats/${id}/`);
      
      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching beat:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch beat'
      };
    }
  },

  // Update Beat
  updateBeat: async (id, beatData) => {
    try {
      const response = await apiClient.put(`/api/distributor/beats/${id}/`, beatData);
      
      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: 'Beat updated successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error updating beat:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update beat'
      };
    }
  },

  // Delete Beat
  deleteBeat: async (id) => {
    try {
      const response = await apiClient.delete(`/api/distributor/beats/${id}/`);
      
      if (response.status === 204 || response.data?.success) {
        return {
          success: true,
          message: 'Beat deleted successfully'
        };
      } else {
        return { success: false, error: 'Failed to delete beat' };
      }
    } catch (error) {
      console.error('Error deleting beat:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete beat'
      };
    }
  },

  // FOS Collection Operations
  
  // Get FOS profiles with outstanding amounts
  getFOSProfiles: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        search = '',
        operator = '',
        city = '',
        district = '',
        overdueDays = '',
        startDate = '',
        endDate = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      // Add optional search and filter parameters
      if (search) queryParams.append('search', search);
      if (operator) queryParams.append('operator', operator);
      if (city) queryParams.append('user_city', city);
      if (district) queryParams.append('user_district', district);
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);

      const response = await apiClient.get(`/api/distributor/fos-profiles/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            page: response.data.page,
            limit: response.data.limit,
            has_next: response.data.has_next,
            has_previous: response.data.has_previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / limit),
            currentPage: page,
            pageSize: limit
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching FOS profiles:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch FOS profiles'
      };
    }
  },

  // Record FOS transaction (collection)
  recordFOSTransaction: async (transactionData) => {
    try {
      const payload = {
        fos_profile_id: parseInt(transactionData.fosProfileId),
        amount: parseFloat(transactionData.amount),
        transaction_type: transactionData.transactionType, // DEBIT or CREDIT
        txn_type: transactionData.txnType, // COLLECT or TOPUP
        payment_mode: transactionData.paymentMode, // CASH, UPI, BANK_TRANSFER, CHEQUE, DD
        remark: transactionData.remark || '',
        utr: transactionData.utr || ''
      };

      const response = await apiClient.post('/api/distributor/outstanding/fos/transaction/', payload);

      if (response.data) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Transaction processed successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error recording FOS transaction:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to record FOS transaction'
      };
    }
  },

  // Get FOS outstanding summary
  getFOSOutstandingSummary: async () => {
    try {
      const response = await apiClient.get('/api/distributor/outstanding/fos/summary/?operator=JIO');
      
      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching FOS outstanding summary:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch FOS outstanding summary'
      };
    }
  },

  // Get FOS transaction history (detailed ledger)
  getFOSTransactionHistory: async (params = {}) => {
    try {
      const {
        fos_id,
        start_date = '',
        end_date = '',
        payment_mode = '',
        page = 1,
        limit = 20
      } = params;

      const queryParams = new URLSearchParams({
        fos_id: fos_id.toString(),
        page: page.toString(),
        limit: limit.toString()
      });

      if (start_date) queryParams.append('date_from', start_date);
      if (end_date) queryParams.append('date_to', end_date);
      if (payment_mode) queryParams.append('payment_mode', payment_mode);

      const response = await apiClient.get(`/api/distributor/outstanding/fos/history/${fos_id}/?${queryParams.toString()}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching FOS transaction history:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch FOS transaction history'
      };
    }
  },

  // Export FOS Ledger to Excel/CSV
  exportFOSLedger: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams({
        fos_id: params.fosId.toString(),
        export_format: 'excel',
        date_from: params.startDate,
        date_to: params.endDate
      });

      if (params.paymentMode) {
        queryParams.append('payment_mode', params.paymentMode);
      }

      const response = await apiClient.get(`/api/distributor/fos-outstanding-export/?${queryParams.toString()}`, {
        responseType: 'blob'
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `fos_ledger_${params.fosId}_${params.startDate}_to_${params.endDate}.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting FOS ledger:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to export FOS ledger'
      };
    }
  },

  // UPI Payment CRUD Operations

  // Get UPI payments with pagination and filters
  getUPIPayments: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 10,
        search = '',
        status = '',
        date_from = '',
        date_to = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (search) queryParams.append('search', search);
      if (status) queryParams.append('status', status);
      if (date_from) queryParams.append('date_from', date_from);
      if (date_to) queryParams.append('date_to', date_to);

      const response = await apiClient.get(`/api/distributor/upi-payments/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            summary: response.data.summary,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching UPI payments:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch UPI payments'
      };
    }
  },

  // Get UPI payment by ID
  getUPIPaymentById: async (paymentId) => {
    try {
      const response = await apiClient.get(`/api/distributor/upi-payments/${paymentId}/`);

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching UPI payment details:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch UPI payment details'
      };
    }
  },

  // Create new UPI payment
  createUPIPayment: async (paymentData) => {
    try {
      const payload = {
        utr: paymentData.utr,
        vpa: paymentData.vpa,
        order_id: paymentData.orderId,
        amount: parseFloat(paymentData.amount),
        transaction_date: paymentData.transactionDate
      };

      const response = await apiClient.post('/api/distributor/upi-payments/', payload);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'UPI payment created successfully'
        };
      } else {
        return { success: false, error: response.data?.message || 'Failed to create UPI payment' };
      }
    } catch (error) {
      console.error('Error creating UPI payment:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to create UPI payment'
      };
    }
  },

  // Update UPI payment
  updateUPIPayment: async (paymentId, paymentData) => {
    try {
      const payload = {};
      
      // Only include the fields that are being updated
      if (paymentData.status !== undefined) payload.status = paymentData.status;
      if (paymentData.utr) payload.utr = paymentData.utr;
      if (paymentData.vpa) payload.vpa = paymentData.vpa;
      if (paymentData.orderId) payload.order_id = paymentData.orderId;
      if (paymentData.amount) payload.amount = parseFloat(paymentData.amount);
      if (paymentData.transactionDate) payload.transaction_date = paymentData.transactionDate;
      if (paymentData.isReconciled !== undefined) payload.is_reconciled = paymentData.isReconciled;

      const response = await apiClient.patch(`/api/distributor/upi-payments/${paymentId}/`, payload);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'UPI payment updated successfully'
        };
      } else {
        return { success: false, error: response.data?.message || 'Failed to update UPI payment' };
      }
    } catch (error) {
      console.error('Error updating UPI payment:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to update UPI payment'
      };
    }
  },

  // Delete UPI payment
  deleteUPIPayment: async (paymentId) => {
    try {
      const response = await apiClient.delete(`/api/distributor/upi-payments/${paymentId}/`);

      if (response.status === 204 || response.data?.success) {
        return {
          success: true,
          message: 'UPI payment deleted successfully'
        };
      } else {
        return { success: false, error: 'Failed to delete UPI payment' };
      }
    } catch (error) {
      console.error('Error deleting UPI payment:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to delete UPI payment'
      };
    }
  },

  // Import UPI payments from Excel/CSV
  importUPIPayments: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/api/distributor/upi-payments/import_payments/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'UPI payments imported successfully'
        };
      } else {
        return { success: false, error: response.data?.message || 'Failed to import UPI payments' };
      }
    } catch (error) {
      console.error('Error importing UPI payments:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to import UPI payments'
      };
    }
  },

  // Export UPI payments to Excel
  exportUPIPayments: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);

      const response = await apiClient.get(`/api/distributor/upi-payments/export/?${queryParams.toString()}`, {
        responseType: 'blob'
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `upi_payments_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting UPI payments:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to export UPI payments'
      };
    }
  },

  // Ledger Reports Operations

  // Get ledger reports with filters
  getLedgerReports: async (params = {}) => {
    try {
      const {
        date_from = '',
        date_to = '',
        payment_mode = '',
        retailer_id = '',
        order_id = '',
        utr = '',
        page = 1,
        page_size = 50
      } = params;

      const queryParams = new URLSearchParams();

      if (date_from) queryParams.append('date_from', date_from);
      if (date_to) queryParams.append('date_to', date_to);
      if (payment_mode) queryParams.append('payment_mode', payment_mode);
      if (retailer_id) queryParams.append('retailer_id', retailer_id);
      if (order_id) queryParams.append('order_id', order_id);
      if (utr) queryParams.append('utr', utr);
      queryParams.append('page', page.toString());
      queryParams.append('page_size', page_size.toString());

      const response = await apiClient.get(`/api/distributor/ledger_reports/?${queryParams.toString()}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching ledger reports:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch ledger reports'
      };
    }
  },

  // Export ledger reports to Excel
  exportLedgerReports: async (params = {}) => {
    try {
      const payload = {
        date_from: params.date_from,
        date_to: params.date_to,
        include_summary: params.include_summary === true, // default to false
        payment_mode: params.payment_mode || null,
        retailer_id: params.retailer_id || null,
        order_id: params.order_id || null,
        utr: params.utr || null
      };

      const response = await apiClient.post('/api/distributor/ledger_reports/export/', payload, {
        responseType: 'blob'
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `ledger_report_${params.date_from}_to_${params.date_to}.xlsx`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting ledger reports:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to export ledger reports'
      };
    }
  },

  // Outstanding Request Operations

  // Get outstanding requests with pagination and filters
  getOutstandingRequests: async (params = {}) => {
    try {
      const {
        page = 1,
        limit = 10,
        status = '',
        user_type = '',
        date_from = '',
        date_to = '',
        mobile = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (status) queryParams.append('status', status);
      if (user_type) queryParams.append('user_type', user_type);
      if (date_from) queryParams.append('date_from', date_from);
      if (date_to) queryParams.append('date_to', date_to);
      if (mobile) queryParams.append('mobile', mobile);

      const response = await apiClient.get(`/api/distributor/outstanding/update-requests/?${queryParams.toString()}`);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching outstanding requests:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to fetch outstanding requests'
      };
    }
  },

  // Approve outstanding request
  approveOutstandingRequest: async (id) => {
    try {
      const response = await apiClient.put(`/api/distributor/outstanding/update-request/${id}/approve/`, {});

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Outstanding request approved successfully'
        };
      } else {
        return { success: false, error: response.data?.message || 'Failed to approve request' };
      }
    } catch (error) {
      console.error('Error approving outstanding request:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to approve outstanding request'
      };
    }
  },

  // Reject outstanding request
  rejectOutstandingRequest: async (id, rejectionRemarks) => {
    try {
      const payload = {
        rejection_remarks: rejectionRemarks
      };

      const response = await apiClient.put(`/api/distributor/outstanding/update-request/${id}/reject/`, payload);

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Outstanding request rejected successfully'
        };
      } else {
        return { success: false, error: response.data?.message || 'Failed to reject request' };
      }
    } catch (error) {
      console.error('Error rejecting outstanding request:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || error.message || 'Failed to reject outstanding request'
      };
    }
  },

  // Get Portal Login Status
  getPortalLoginStatus: async () => {
    try {
      const response = await apiClient.get('/api/distributor/portal_login_status/');

      if (response.data) {
        return {
          success: response.data.success,
          portalLoginStatus: response.data.portal_login_status
        };
      } else {
        return { success: false, portalLoginStatus: null };
      }
    } catch (error) {
      console.error('Error fetching portal login status:', error);
      return {
        success: false,
        portalLoginStatus: null
      };
    }
  },

  // Export outstanding requests
  exportOutstandingRequests: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();

      if (params.status) queryParams.append('status', params.status);
      if (params.user_type) queryParams.append('user_type', params.user_type);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.mobile) queryParams.append('mobile', params.mobile);

      const response = await apiClient.get(`/api/distributor/outstanding/update-requests/export/?${queryParams.toString()}`, {
        responseType: 'blob'
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = `outstanding_requests_${new Date().toISOString().split('T')[0]}.xlsx`;

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error exporting outstanding requests:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to export outstanding requests'
      };
    }
  },

  // ============================================
  // Plan & Subscription Operations
  // ============================================

  // Get available plans (active only)
  getPlans: async (params = {}) => {
    try {
      const {
        page = 1,
        page_size = 20,
        billing_cycle = ''
      } = params;

      const queryParams = new URLSearchParams({
        page: page.toString(),
        page_size: page_size.toString()
      });

      if (billing_cycle) queryParams.append('billing_cycle', billing_cycle);

      const response = await apiClient.get(`/api/distributor/plans/?${queryParams.toString()}`);

      if (response.data) {
        return {
          success: true,
          data: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            results: response.data.results,
            totalPages: Math.ceil(response.data.count / page_size),
            currentPage: page,
            pageSize: page_size
          }
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch plans'
      };
    }
  },

  // Get plan by ID
  getPlanById: async (planId) => {
    try {
      const response = await apiClient.get(`/api/distributor/plans/${planId}/`);

      if (response.data) {
        return {
          success: true,
          data: response.data
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error fetching plan details:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch plan details'
      };
    }
  },

  // Get current subscription
  getMySubscription: async () => {
    try {
      const response = await apiClient.get('/api/distributor/subscription/');

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'No subscription data received'
        };
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch subscription'
      };
    }
  },

  // Subscribe to a plan
  subscribeToPlan: async (planId) => {
    try {
      const response = await apiClient.post('/api/distributor/subscribe/', {
        plan_id: planId
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Subscription initiated successfully'
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'Failed to subscribe to plan'
        };
      }
    } catch (error) {
      console.error('Error subscribing to plan:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to subscribe to plan'
      };
    }
  },

  // Payment success callback
  paymentSuccess: async (subscriptionId, transactionId, paymentMethod = 'online') => {
    try {
      const response = await apiClient.post('/api/distributor/payment/success/', {
        subscription_id: subscriptionId,
        transaction_id: transactionId,
        payment_method: paymentMethod
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Payment processed successfully'
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'Payment processing failed'
        };
      }
    } catch (error) {
      console.error('Error processing payment success:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to process payment'
      };
    }
  },

  // Payment failure callback
  paymentFailure: async (subscriptionId, errorMessage = '') => {
    try {
      const response = await apiClient.post('/api/distributor/payment/failure/', {
        subscription_id: subscriptionId,
        error_message: errorMessage
      });

      if (response.data && response.data.success) {
        return {
          success: true,
          data: response.data.data,
          message: response.data.message || 'Payment failure recorded'
        };
      } else {
        return {
          success: false,
          error: response.data?.message || 'Failed to record payment failure'
        };
      }
    } catch (error) {
      console.error('Error recording payment failure:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to record payment failure'
      };
    }
  },

  // ============================================
  // Retailer Import Operations
  // ============================================

  // Import retailers from Excel
  importRetailers: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('/api/distributor/retailers/import/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data) {
        return {
          success: true,
          data: response.data,
          message: response.data.message || 'Retailers imported successfully'
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error importing retailers:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to import retailers'
      };
    }
  },

  // Download retailer import template
  downloadRetailerImportTemplate: async () => {
    try {
      const response = await apiClient.get('/api/distributor/retailers/import/template/', {
        responseType: 'blob'
      });

      if (response.data) {
        const contentDisposition = response.headers['content-disposition'];
        let filename = 'retailer_import_template.xlsx';

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        return {
          success: true,
          data: response.data,
          fileName: filename
        };
      } else {
        return { success: false, error: 'No data received' };
      }
    } catch (error) {
      console.error('Error downloading retailer import template:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to download template'
      };
    }
  }

};

export default distributorService;